import {useCallback, useMemo} from 'react';
import {Iterable} from 'immutable';
import type {QuestionModelV3} from '@albert-io/models';
import {GuessModelV1} from '@albert-io/models';
import type {QuestionTypes} from './useQuestionStores';
import {setUpQuestionStore, isFitbItemBank} from './useQuestionStores';
import {allSaveStatuses} from '../QuestionType.types';
import type {DraftInfo} from './DraftGuess';
import {useDraftGuessStore} from './DraftGuess';
import sessionStore from 'client/Session/SessionStore';

const useQuestionAndDrafts = (activeQuestion: QuestionModelV3) => {
  const params = global.location.pathname.split('/');
  let assignmentId: string | null = null;

  if (params.some((param) => param === 'assignment')) {
    assignmentId = params[params.length - 1];
  }
  const useStoreHook = useMemo(() => {
    const questionType = activeQuestion?.getType?.() as QuestionTypes;
    const storeName = activeQuestion?.getStoreName?.() || questionType;
    if (isFitbItemBank(questionType)) {
      return setUpQuestionStore<'item-bank'>(questionType, storeName);
    } else {
      return setUpQuestionStore<'ordering'>(questionType, storeName);
    }
  }, [activeQuestion]);

  // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const addQuestionInfo = useStoreHook((state) => state.addQuestionInfo);
  // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const buildGuessContent = useStoreHook((state) => state.buildGuessContent);

  // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const question = useStoreHook((state) => state.question);
  // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const isSubmitted = useStoreHook((state) => state.isSubmitted);
  // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const addGuessInfo = useStoreHook((state) => state.addGuessInfo);
  const draftByQuestionId = useDraftGuessStore((state) => state.draftByQuestionId);
  const buildTimeElapsed = useDraftGuessStore((state) => state.buildTimeElapsed);
  const saveDraftGuessFunction = useDraftGuessStore((state) => state.saveDraftGuess);

  const initialize = useCallback(
    (questionArg: Record<string, any>) => {
      if (questionArg && typeof questionArg.getId === 'function') {
        addQuestionInfo(questionArg);
      }
    },
    [addQuestionInfo]
  );

  const draftGuessInfo: DraftInfo | null = useMemo(() => {
    if (!question || !Iterable.isIterable(question) || question.size === 0) return null;
    try {
      const allDrafts = draftByQuestionId;
      const questionId = question.getId();
      return allDrafts[questionId] || null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }, [draftByQuestionId, question]);

  const hasDraftGuess: boolean = useMemo(() => {
    try {
      let hasDraft = false;
      if (draftGuessInfo && !!draftGuessInfo.draft && draftGuessInfo.draft !== null) {
        hasDraft = true;
      }
      return hasDraft;
    } catch (err) {
      console.log(err);
      return false;
    }
  }, [draftGuessInfo]);

  const getDraftStatus = useCallback(() => {
    if (!draftGuessInfo) return 'default';
    return draftGuessInfo.saveStatus;
  }, [draftGuessInfo]);

  const getDraftStatusCode = useCallback(() => {
    const status = getDraftStatus();
    const code = allSaveStatuses.indexOf(status);
    return code;
  }, [getDraftStatus]);

  const saveDraftGuess = useCallback(() => {
    if (isSubmitted) return;
    saveDraftGuessFunction(assignmentId, question?.getId?.(), buildGuessContent());
  }, [saveDraftGuessFunction, question, assignmentId, buildGuessContent, isSubmitted]);

  const buildGuess = useCallback(
    ({
      assignmentId: assignmentIdParam,
      practiceId,
      diagnosticId,
      content: contentOverride
    }: {
      assignmentId?: string | null;
      practiceId?: string;
      diagnosticId?: string;
      content?: Record<string, unknown>;
    }) => {
      const questionId = question.getId();
      const timeElapsed = buildTimeElapsed(questionId);
      if (typeof timeElapsed !== 'number') {
        throw new Error('`timeElapsed` is missing from guess content.');
      }
      const guessData = {
        content: contentOverride ?? buildGuessContent(),
        time_elapsed: timeElapsed,
        ...(practiceId && {adaptive_practice_id: practiceId}),
        ...(assignmentIdParam && {assignment_id: assignmentIdParam}),
        ...(diagnosticId && {diagnostic_id: diagnosticId})
      };

      return new GuessModelV1(guessData)
        .updateRelationship('question_v3', questionId)
        .updateRelationship('student_v2', sessionStore.getUserId())
        .updateRelationship('assignment_v3', assignmentIdParam, !!assignmentIdParam);
    },
    [buildGuessContent, buildTimeElapsed, question]
  );

  return {
    draftByQuestionId,
    hasDraftGuess,
    initialize,
    assignmentId,
    buildGuess,
    saveDraftGuess,
    isSubmitted,
    addGuessInfo,
    getDraftStatus,
    getDraftStatusCode
  };
};

export default useQuestionAndDrafts;
